import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../utilities/axios";
import downalodableFile from "../utilities/downloadableFile";

const initialState = {
  status: "idle",
  error: null,
};

export const exportEmployeesBalances = createAsyncThunk("export/employees-balances", async () => {
  let data;
  try {
    const response = await axios.get(`/export`,{responseType:"blob"});
    data = await response.data;
    if ((response.status === 200)) {
        downalodableFile(response.data,'employeesBalances', 'xlsx')
    }
    throw new Error(response.statusText);
  } catch (err) {
    return Promise.reject(err.message ? err.message : err);
  }
});

export const exportAnnualLeaveRegister = createAsyncThunk("export/annual-leave-register", async ({ userId, salaryFile }) => {
  let data;
  try {
    let response;
    
    if (salaryFile) {
      // Use POST with FormData when file is provided
      const formData = new FormData();
      formData.append('salaryFile', salaryFile);
      response = await axios.post(`/export/annual-leave-register/${userId}`, formData, {
        responseType: "blob",
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } else {
      // Use GET when no file is provided (backward compatible)
      response = await axios.get(`/export/annual-leave-register/${userId}`, {responseType:"blob"});
    }
    
    data = await response.data;
    if ((response.status === 200)) {
        downalodableFile(response.data, 'annual-leave-register', 'docx');
        return { success: true };
    }
    throw new Error(response.statusText);
  } catch (err) {
    return Promise.reject(err.message ? err.message : err);
  }
});

export const exportAllEmployeesAnnualLeaveRegister = createAsyncThunk("export/all-employees-annual-leave-register", async (salaryFile) => {
  let data;
  try {
    let response;
    
    if (salaryFile) {
      // Use POST with FormData when file is provided
      const formData = new FormData();
      formData.append('salaryFile', salaryFile);
      response = await axios.post(`/export/annual-leave-register`, formData, {
        responseType: "blob",
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } else {
      // Use GET when no file is provided (backward compatible)
      response = await axios.get(`/export/annual-leave-register`, {responseType:"blob"});
    }
    
    data = await response.data;
    if ((response.status === 200)) {
        downalodableFile(response.data, 'all-employees-annual-leave-register', 'docx');
        return { success: true };
    }
    throw new Error(response.statusText);
  } catch (err) {
    return Promise.reject(err.message ? err.message : err);
  }
});

export const exportLeaves = createAsyncThunk("export/leaves", async (filters) => {
  let data;
  try {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.department) params.append('department', filters.department);
    if (filters.type) params.append('type', filters.type);
    if (filters.dateStartFrom) params.append('dateStartFrom', filters.dateStartFrom);
    if (filters.dateStartTo) params.append('dateStartTo', filters.dateStartTo);
    if (filters.dateEndFrom) params.append('dateEndFrom', filters.dateEndFrom);
    if (filters.dateEndTo) params.append('dateEndTo', filters.dateEndTo);
    if (filters.userId) params.append('userId', filters.userId);
    if (filters.headquarterId) params.append('headquarterId', filters.headquarterId);
    if (filters.month) params.append('month', filters.month);
    if (filters.year) params.append('year', filters.year);

    const queryString = params.toString();
    const url = `/export/leaves${queryString ? `?${queryString}` : ''}`;
    
    const response = await axios.get(url, {responseType:"blob"});
    data = await response.data;
    if ((response.status === 200)) {
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
        downalodableFile(response.data, `leaves-export-${timestamp}`, 'xlsx');
        return { success: true };
    }
    throw new Error(response.statusText);
  } catch (err) {
    return Promise.reject(err.message ? err.message : err);
  }
});

export const exportLeavesMonthlySummary = createAsyncThunk("export/leaves-monthly-summary", async (filters) => {
  let data;
  try {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.department) params.append('department', filters.department);
    if (filters.type) params.append('type', filters.type);
    if (filters.dateStartFrom) params.append('dateStartFrom', filters.dateStartFrom);
    if (filters.dateStartTo) params.append('dateStartTo', filters.dateStartTo);
    if (filters.dateEndFrom) params.append('dateEndFrom', filters.dateEndFrom);
    if (filters.dateEndTo) params.append('dateEndTo', filters.dateEndTo);
    if (filters.userId) params.append('userId', filters.userId);
    if (filters.headquarterId) params.append('headquarterId', filters.headquarterId);
    if (filters.month) params.append('month', filters.month);
    if (filters.year) params.append('year', filters.year);

    const queryString = params.toString();
    const url = `/export/leaves/monthly-summary${queryString ? `?${queryString}` : ''}`;

    const response = await axios.get(url, { responseType: "blob" });
    data = await response.data;
    if ((response.status === 200)) {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      downalodableFile(response.data, `leaves-monthly-summary-${timestamp}`, 'xlsx');
      return { success: true };
    }
    throw new Error(response.statusText);
  } catch (err) {
    return Promise.reject(err.message ? err.message : err);
  }
});

const slice = createSlice({
  name: "user",
  initialState,
  reducers: {},
  extraReducers: {
    [exportEmployeesBalances.pending]: (state) => {
      state.status = "loading";
    },
    [exportEmployeesBalances.fulfilled]: (state, action) => {
      state.status = "succeeded";
    },
    [exportEmployeesBalances.rejected]: (state, action) => {
      state.status = "failed";
      state.error = action.payload;
    },
    [exportAnnualLeaveRegister.pending]: (state) => {
      state.status = "loading";
    },
    [exportAnnualLeaveRegister.fulfilled]: (state, action) => {
      state.status = "succeeded";
    },
    [exportAnnualLeaveRegister.rejected]: (state, action) => {
      state.status = "failed";
      state.error = action.payload;
    },
    [exportAllEmployeesAnnualLeaveRegister.pending]: (state) => {
      state.status = "loading";
    },
    [exportAllEmployeesAnnualLeaveRegister.fulfilled]: (state, action) => {
      state.status = "succeeded";
    },
    [exportAllEmployeesAnnualLeaveRegister.rejected]: (state, action) => {
      state.status = "failed";
      state.error = action.payload;
    },
    [exportLeaves.pending]: (state) => {
      state.status = "loading";
    },
    [exportLeaves.fulfilled]: (state, action) => {
      state.status = "succeeded";
    },
    [exportLeaves.rejected]: (state, action) => {
      state.status = "failed";
      state.error = action.payload;
    },
    [exportLeavesMonthlySummary.pending]: (state) => {
      state.status = "loading";
    },
    [exportLeavesMonthlySummary.fulfilled]: (state, action) => {
      state.status = "succeeded";
    },
    [exportLeavesMonthlySummary.rejected]: (state, action) => {
      state.status = "failed";
      state.error = action.payload;
    },
  },
});
export const {} = slice.actions;

export const reducer = slice.reducer;

export default slice;
